import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "../utils";

const buttonVariants = cva(
  "inline-flex items-center w-full gap-2 whitespace-nowrap text-base font-medium  border-black ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-10 px-6 py-8",
        destructive:
          "justify-center bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "justify-center border-b border-black bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "justify-center bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "justify-center hover:bg-accent hover:text-accent-foreground",
        link: "justify-center text-primary underline-offset-4 hover:underline",
        warning: "bg-fikseRed border-b border-black text-sm text-black text-left p-4 ",
        warningClickable:
          "bg-fikseRed border-b border-black text-sm text-black text-left p-4 hover:bg-fikseRed/90",
        blue: "w-full border-b border-black bg-fikseBlue p-4 pl-6 text-left text-sm hover:bg-fikseBlue/90",
        red: "w-full border-b border-black bg-fikseRed p-4 pl-6 text-left text-sm hover:bg-fikseRed/90",
        white:
          "w-full border-b border-black bg-white p-4 pl-6 text-left text-sm hover:bg-gray-100",
      },
      size: {
        warning: "",
        default: "",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
